/*
 * @author BSG <dev@bsgroup.eu>
 * @copyright Better Software Group S.A.
 * @version: 1.0
 */
import { useCallback, useEffect } from "react";

import { useDeviceType } from "../../shared/hooks/useDeviceType";
import { useToggleFullScreen } from "../../shared/hooks/useToggleFullScreen";
import { usePlayerController } from "../BasePlayer";

export const usePlayerFullScreen = () => {
  const { iPhoneDevice } = useDeviceType();
  const { containerRef, player } = usePlayerController();

  const { toggle, fullScreen: isFullScreen } = useToggleFullScreen(
    containerRef.current,
  );

  const reset = useCallback((): Promise<void> => {
    if (iPhoneDevice && player.isNativeFullScreen()) {
      return player.exitNativeFullScreen();
    }

    if (isFullScreen) {
      return toggle();
    }

    return Promise.resolve();
  }, [iPhoneDevice, isFullScreen, player, toggle]);

  const wrappedToggle = useCallback(async () => {
    // iPhone does not support full screen for custom elements, in that case
    // we need to use native video full screen that will show native player UI
    try {
      iPhoneDevice ? await player.toggleNativeFullScreen() : await toggle();
    } catch (e) {
      console.warn("Unable to enter full screen mode");
    }
  }, [iPhoneDevice, player, toggle]);

  useEffect(() => {
    isFullScreen ? player.onEnterFullScreen() : player.onLeaveFullScreen();
  }, [player, isFullScreen]);

  return {
    toggle: wrappedToggle,
    reset,
    isFullScreen,
  };
};
